import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useItemsBoardStore } from '@/stores/items-board-store'
import { useCustomerTrackerStore } from '@/stores/customer-tracker-store'
import type { ItemCard } from '@/types/dashboard'

// Sync goals to Supabase
export function useGoalsSync() {
  // Load goals from Supabase on mount ONCE
  useEffect(() => {
    async function loadGoals() {
      const { data, error } = await supabase
        .from('office_tv_goals')
        .select('*')
        .order('order_num', { ascending: true })

      if (error) {
        console.error('Error loading goals:', error)
        return
      }

      if (data && data.length > 0) {
        // Map from DB format to app format
        const items: ItemCard[] = data.map((goal) => ({
          id: goal.id,
          title: goal.title,
          description: goal.description || undefined,
          // Handle both JSON array and single priority
          priority: typeof goal.priority === 'string' && goal.priority.startsWith('[')
            ? JSON.parse(goal.priority)
            : goal.priority,
          status: goal.status as any,
          completed: goal.completed,
          order: goal.order_num,
          createdAt: goal.created_at,
          completedAt: goal.completed_at || undefined,
        }))

        useItemsBoardStore.setState({ items })
      }
    }

    loadGoals()
  }, []) // Empty deps - only load once on mount
}

// Sync customers to Supabase
export function useCustomersSync() {
  // Load customers from Supabase on mount ONCE
  useEffect(() => {
    async function loadCustomers() {
      const { data: customersData, error: customersError } = await supabase
        .from('office_tv_customers')
        .select('*')

      if (customersError) {
        console.error('Error loading customers:', customersError)
        return
      }

      if (customersData && customersData.length > 0) {
        // Load timeline entries for all customers
        const { data: timelineData, error: timelineError } = await supabase
          .from('office_tv_customer_timeline')
          .select('*')

        if (timelineError) {
          console.error('Error loading timeline:', timelineError)
        }

        // Map customers with their timeline entries
        const customersWithTimeline = customersData.map((customer) => ({
          id: customer.id,
          userId: customer.user_id || '',
          name: customer.name,
          position: {
            x: customer.position_x ?? 100,
            y: customer.position_y ?? 100,
          },
          // Handle case where columns don't exist yet in DB
          size: (customer.card_width && customer.card_height)
            ? { width: customer.card_width, height: customer.card_height }
            : { width: 140, height: 44 },
          needsFollowUp: customer.needs_follow_up,
          lastUpdated: customer.updated_at,
          currentDescription: customer.current_description || undefined,
          timeline:
            timelineData
              ?.filter((entry) => entry.customer_id === customer.id)
              .map((entry) => ({
                id: entry.id,
                date: entry.date,
                note: entry.note,
                type: entry.type as 'past' | 'future',
                createdAt: entry.created_at,
              }))
              .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()) || [],
        }))

        useCustomerTrackerStore.setState({ customers: customersWithTimeline })
      }
    }

    loadCustomers()
  }, []) // Empty deps - only load once on mount
}

// Sync group boxes from Supabase
export function useGroupBoxesSync() {
  // Load group boxes from Supabase on mount ONCE
  useEffect(() => {
    async function loadGroupBoxes() {
      const { data, error } = await supabase
        .from('office_tv_group_boxes')
        .select('*')

      if (error) {
        // If table doesn't exist, just skip
        if (error.code === '42P01' || error.message?.includes('relation')) {
          console.warn('office_tv_group_boxes table not found')
          return
        }
        console.error('Error loading group boxes:', error)
        return
      }

      if (data && data.length > 0) {
        const groupBoxes = data.map((box) => ({
          id: box.id,
          label: box.label,
          position: {
            x: box.position_x ?? 0,
            y: box.position_y ?? 0,
          },
          size: {
            width: box.box_width ?? 200,
            height: box.box_height ?? 200,
          },
          color: box.color || '#ffffff',
        }))

        useCustomerTrackerStore.setState({ groupBoxes })
      }
    }

    loadGroupBoxes()
  }, []) // Empty deps - only load once on mount
}
